import React, { useState, useEffect } from 'react';
import './VirtualCafe.css';
import RoomCard from './components/RoomCard';
import RoomCreationForm from './components/RoomCreationForm';
import VideoChatRoom from './components/VideoChatRoom';
import { useAuth } from '../../contexts/authContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import supabase from "../../supabaseClient";
import RoomDetailsModal from './components/RoomDetailsModal';
import RoomModal from './components/RoomModal';

const VirtualCafe = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [eventRooms, setEventRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [joiningRoomId, setJoiningRoomId] = useState(null);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [error, setError] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [nativeLanguageFilter, setNativeLanguageFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [languageOptions, setLanguageOptions] = useState([]);
  const [nativeLanguageOptions, setNativeLanguageOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('cafe'); // 'cafe' | 'groups' | 'events'
  const [userGroupIds, setUserGroupIds] = useState(new Set());
  const [userEventIds, setUserEventIds] = useState(new Set());
  const [selectedDetailRoom, setSelectedDetailRoom] = useState(null);
  const location = useLocation();
  const { roomId: routeRoomId } = useParams();

  if (loading) return <div>Loading...</div>;
  if (!user) {
    navigate('/login');
    return null;
  }

  // Fetch all events (for Event Rooms tab) and track which ones the user is attending
  useEffect(() => {
    const fetchEventRooms = async () => {
      if (!user) return;
      try {
        // Track which events the user is attending
        const { data: attendance, error: attendErr } = await supabase
          .from('event_attendees')
          .select('event_id')
          .eq('user_id', user.id);
        if (attendErr) throw attendErr;

        const joinedEventIds = new Set((attendance || []).map(a => a.event_id));
        setUserEventIds(joinedEventIds);

        // Fetch all events (optionally filter to upcoming later)
        const { data: events, error: eventsErr } = await supabase
          .from('events')
          .select('*, event_banner_url');
        if (eventsErr) throw eventsErr;

        const eventRooms = (events || []).map(event => {
          const resolvedRoomId = event.room_id || event.id;

          return {
            id: resolvedRoomId,
            name: event.title + ' (Event Room)',
            language: event.language,
            native_language: event.language,
            level: event.level || '',
            topic: event.description || '',
            date: event.date || '',
            time: event.time || '',
            maxParticipants: 20,
            attendees: event.attendees || 1,
            isEvent: true,
            eventId: event.id,
            isHost: event.created_by === user.id,
            hasLinkedRoom: !!event.room_id,
            event_banner_url: event.event_banner_url || null,
          };
        });
        setEventRooms(eventRooms);
      } catch (e) {
        setError(e.message || 'Failed to load event rooms');
        setEventRooms([]);
      }
    };
    fetchEventRooms();
  }, [user]);

  // Fetch group memberships for this user
  useEffect(() => {
    const fetchGroupMemberships = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id);
        if (error) throw error;
        const ids = new Set((data || []).map(row => row.group_id));
        setUserGroupIds(ids);
      } catch {
        setUserGroupIds(new Set());
      }
    };
    fetchGroupMemberships();
  }, [user]);

  // Fetch regular rooms from Supabase
  useEffect(() => {
    const fetchRooms = async () => {
      setRoomsLoading(true);
      try {
        const { data: roomsData, error } = await supabase
          .from('rooms')
          .select('*');
        if (error) throw error;
        const roomIds = (roomsData || []).map(r => r.id);
        let countsByRoom = {};
        if (roomIds.length > 0) {
          // Prefer RPC for aggregated counts, fallback to client aggregation
          try {
            const { data: countsData, error: rpcErr } = await supabase
              .rpc('room_participant_counts', { room_ids: roomIds });
            if (rpcErr) throw rpcErr;
            if (countsData) {
              countsData.forEach(row => { countsByRoom[row.room_id] = row.count || 0; });
            }
          } catch (e) {
            const { data: participantsAll } = await supabase
              .from('room_participants')
              .select('room_id')
              .in('room_id', roomIds);
            (participantsAll || []).forEach(p => {
              countsByRoom[p.room_id] = (countsByRoom[p.room_id] || 0) + 1;
            });
          }
        }
        // Fetch preview participants (first 3) with usernames/emails
        let previewsByRoom = {};
        if (roomIds.length > 0) {
          const { data: previewRows } = await supabase
            .from('room_participants')
            .select(`
            room_id,
            profiles:user_id (
              display_name,
              name,
              email,
              avatar_url
            )
          `)
            .in('room_id', roomIds);
          (previewRows || []).forEach(row => {
            const prof = row.profiles || {};
            const name = prof.display_name || prof.name || prof.email || '?';
            if (!previewsByRoom[row.room_id]) previewsByRoom[row.room_id] = [];
            if (previewsByRoom[row.room_id].length < 3) {
              previewsByRoom[row.room_id].push({
                name,
                avatar_url: prof.avatar_url || null,
                initial: name[0]?.toUpperCase() || '?',
              });
            }
          });
        }

        // If some rooms are linked to groups, fetch their banners so Group Rooms can show a cover image
        let groupMetaById = {};
        const groupIds = (roomsData || [])
          .map(r => r.group_id)
          .filter(Boolean);
        if (groupIds.length > 0) {
          const { data: groupRows } = await supabase
            .from('groups')
            .select('id, group_banner_url')
            .in('id', groupIds);
          (groupRows || []).forEach(g => {
            groupMetaById[g.id] = {
              group_banner_url: g.group_banner_url || null,
            };
          });
        }

        const roomsWithParticipants = (roomsData || []).map(room => ({
          ...room,
          participants: countsByRoom[room.id] || 0,
          previewParticipants: previewsByRoom[room.id] || [],
          isEvent: false,
          // Reuse RoomCard's event_banner_url prop for group covers as well, and allow cafe-specific banners
          event_banner_url:
            room.event_banner_url
            || room.room_banner_url
            || (room.group_id ? groupMetaById[room.group_id]?.group_banner_url || null : null),
        }));
        setRooms(roomsWithParticipants);

        // Dynamically extract unique languages and native languages from rooms
        const langs = new Set();
        const nativeLangs = new Set();
        roomsWithParticipants.forEach(room => {
          if (room.language) langs.add(room.language);
          // If you have a native_language field on rooms, use it here
          if (room.native_language) nativeLangs.add(room.native_language);
        });
        setLanguageOptions(Array.from(langs));
        setNativeLanguageOptions(Array.from(nativeLangs));
        setRoomsLoading(false);
      } catch (e) {
        setError(e.message || 'Failed to load rooms');
        setRooms([]);
        setLanguageOptions([]);
        setNativeLanguageOptions([]);
        setRoomsLoading(false);
      }
    };
    fetchRooms();
  }, []);

  // Subscribe list-level to participant changes and refresh counts
  useEffect(() => {
    const channel = supabase
      .channel('vc-room-participants-list')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'room_participants' },
        () => {
          // Re-run rooms fetch for counts (lightweight since only counts are refreshed)
          (async () => {
            const { data: roomsData } = await supabase.from('rooms').select('*');
            const roomIds = (roomsData || []).map(r => r.id);
            let countsByRoom = {};
            if (roomIds.length > 0) {
              try {
                const { data: countsData, error: rpcErr } = await supabase
                  .rpc('room_participant_counts', { room_ids: roomIds });
                if (rpcErr) throw rpcErr;
                if (countsData) {
                  countsData.forEach(row => { countsByRoom[row.room_id] = row.count || 0; });
                }
              } catch {
                const { data: participantsAll } = await supabase
                  .from('room_participants')
                  .select('room_id')
                  .in('room_id', roomIds);
                (participantsAll || []).forEach(p => {
                  countsByRoom[p.room_id] = (countsByRoom[p.room_id] || 0) + 1;
                });
              }
            }
            // Also refresh preview participants
            let previewsByRoom = {};
            if (roomIds.length > 0) {
              const { data: previewRows } = await supabase
                .from('room_participants')
                .select(`
                  room_id,
                  profiles:user_id (
                    display_name,
                    name,
                    email,
                    avatar_url
                  )
                `)
                .in('room_id', roomIds);
              (previewRows || []).forEach(row => {
                const prof = row.profiles || {};
                const name = prof.display_name || prof.name || prof.email || '?';
                if (!previewsByRoom[row.room_id]) previewsByRoom[row.room_id] = [];
                if (previewsByRoom[row.room_id].length < 3) {
                  previewsByRoom[row.room_id].push({
                    name,
                    avatar_url: prof.avatar_url || null,
                    initial: name[0]?.toUpperCase() || '?',
                  });
                }
              });
            }

            const roomsWithParticipants = (roomsData || []).map(room => ({
              ...room,
              participants: countsByRoom[room.id] || 0,
              previewParticipants: previewsByRoom[room.id] || [],
              isEvent: false,
              event_banner_url:
                room.event_banner_url
                || room.room_banner_url
                || (room.group_id ? groupMetaById[room.group_id]?.group_banner_url || null : null),
            }));
            setRooms(roomsWithParticipants);
          })();
        }
      )
      .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch { } };
  }, [supabase]);

  // Merge event rooms and regular rooms (for join logic and active room lookup)
  const allRooms = [...eventRooms, ...rooms];

  // Split rooms into categories
  const groupRooms = rooms.filter(r => r.group_id);
  const cafeRooms = rooms.filter(r => !r.group_id && !r.event_id);

  // Filtering helper
  const applyFilters = list => list.filter(room => {
    if (languageFilter && room.language !== languageFilter) return false;
    if (nativeLanguageFilter && room.native_language !== nativeLanguageFilter) return false;
    if (levelFilter && room.level !== levelFilter) return false;
    return true;
  });

  const filteredEventRooms = applyFilters(eventRooms);
  const filteredGroupRooms = applyFilters(groupRooms);
  const filteredCafeRooms = applyFilters(cafeRooms);

  // Room join handler
  const handleJoinRoom = async (roomId) => {
    setJoiningRoomId(roomId);
    const roomObj = allRooms.find(r => String(r.id) === String(roomId));
    // For event rooms, skip Supabase persistence and open directly
    if (roomObj?.isEvent) {
      setActiveRoom(roomId);
      try { navigate(`/app/virtualcafe/${roomId}`, { replace: false }); } catch { }
      setJoiningRoomId(null);
      return;
    }
    // Add user to room_participants if not already present
    try {
      const { data: existing } = await supabase
        .from('room_participants')
        .select('*')
        .eq('room_id', roomId)
        .eq('user_id', user.id)
        .single();

      if (!existing) {
        await supabase.from('room_participants').insert([
          { room_id: roomId, user_id: user.id }
        ]);
      }
      setTimeout(() => {
        setActiveRoom(roomId);
        // Update URL for deep-link
        try { navigate(`/app/virtualcafe/${roomId}`, { replace: false }); } catch { }
        setJoiningRoomId(null);
      }, 500);
    } catch (e) {
      setError(e.message || 'Failed to join room');
      setJoiningRoomId(null);
    }
  };

  // Room creation handler
  const handleCreateRoom = async (roomData) => {
    // Insert new room
    try {
      // Optional: upload banner to shared 'banners' bucket
      let room_banner_url = null;
      if (roomData.bannerFile) {
        const fileExt = roomData.bannerFile.name.split('.').pop();
        const fileName = `room-banner_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${fileExt}`;
        const filePath = `room-banners/${fileName}`;
        const { error: uploadError } = await supabase.storage
          .from('banners')
          .upload(filePath, roomData.bannerFile, { upsert: true });
        if (uploadError) {
          console.error('Error uploading room banner:', uploadError);
        } else {
          const { data } = supabase.storage.from('banners').getPublicUrl(filePath);
          room_banner_url = data?.publicUrl || null;
        }
      }

      const { data: newRoom, error } = await supabase
        .from('rooms')
        .insert([
          {
            name: roomData.name,
            language: roomData.language,
            native_language: roomData.native_language, // Add this if your form collects it
            level: roomData.level,
            created_by: user.id,
            description: roomData.description || null,
            room_banner_url,
          }
        ])
        .select()
        .single();
      if (error || !newRoom) throw error || new Error('Failed to create room');

      // Add creator as participant
      await supabase.from('room_participants').insert([
        { room_id: newRoom.id, user_id: user.id }
      ]);

      // Update local state and open the room
      setRooms(prev => [...prev, { ...newRoom, participants: 1, isEvent: false }]);
      setActiveRoom(newRoom.id);
      setIsCreatingRoom(false);
      navigate(`/app/virtualcafe/${newRoom.id}`, { replace: false });
    } catch (e) {
      setError(e.message || 'Failed to create room');
    }
  };

  // Room leave handler
  const handleLeaveRoom = () => {
    setActiveRoom(null);
    // Return to base Virtual Cafe URL
    try { navigate('/app/virtualcafe', { replace: true }); } catch { }
  };

  // Sync URL params to active room (supports /app/virtualcafe/:roomId and ?eventRoom=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eventRoomId = params.get('eventRoom');
    // Priority: explicit :roomId first, then ?eventRoom
    if (routeRoomId) {
      setActiveRoom(routeRoomId);
      return;
    }
    if (eventRoomId && eventRooms.some(r => r.id === eventRoomId)) {
      setActiveRoom(eventRoomId);
    }
  }, [routeRoomId, location.search, eventRooms]);

  const selectedRoom = allRooms.find(r => String(r.id) === String(activeRoom));

  const handleSeeDetails = (room) => {
    setSelectedDetailRoom(room);
  };

  const handleModalJoin = (room) => {
    setSelectedDetailRoom(null);
    if (room.roomType === 'group') {
      // Deep-link to the specific group in a new tab so the user lands on its About tab
      const target = room.group_id ? `/app/groups/${room.group_id}` : '/app/groups';
      window.open(target, '_blank', 'noopener,noreferrer');
    } else if (room.roomType === 'event') {
      // Deep-link to the specific event in a new tab so the user lands on its About tab
      const target = room.eventId
        ? `/app/events/${room.eventId}`
        : room.id
          ? `/app/events/${room.id}`
          : '/app/events';
      window.open(target, '_blank', 'noopener,noreferrer');
    } else {
      handleJoinRoom(room.id);
    }
  };

  return (
    <div className="virtual-cafe-section">
      <div className="virtual-cafe-panel page-panel">
        <h1>Virtual Language Café</h1>
        <p className="section-description">
          Join virtual rooms to practice languages with other learners through video chat.
        </p>
        {error && <div className="vc-error-banner" role="alert">{error}</div>}

        {!activeRoom && !isCreatingRoom && (
          <>
            <div className="vc-tabs">
              <button
                type="button"
                className={`vc-tab ${activeTab === 'cafe' ? 'active' : ''}`}
                onClick={() => setActiveTab('cafe')}
              >
                Café Rooms
              </button>
              <button
                type="button"
                className={`vc-tab ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={() => setActiveTab('groups')}
              >
                Group Rooms
              </button>
              <button
                type="button"
                className={`vc-tab ${activeTab === 'events' ? 'active' : ''}`}
                onClick={() => setActiveTab('events')}
              >
                Event Rooms
              </button>
            </div>

            <div className="cafe-actions">
              <button className="create-room-button" onClick={() => setIsCreatingRoom(true)}>
                Create New Room
              </button>
              <div className="filter-options">
                <select
                  className="filter-select"
                  value={languageFilter}
                  onChange={e => setLanguageFilter(e.target.value)}
                >
                  <option value="">All Languages</option>
                  {languageOptions.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
                <select
                  className="filter-select"
                  value={nativeLanguageFilter}
                  onChange={e => setNativeLanguageFilter(e.target.value)}
                >
                  <option value="">All Native Languages</option>
                  {nativeLanguageOptions.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
                <select
                  className="filter-select"
                  value={levelFilter}
                  onChange={e => setLevelFilter(e.target.value)}
                >
                  <option value="">All Levels</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Mixed">Mixed</option>
                </select>
              </div>
            </div>
            <div className="rooms-grid">
              {roomsLoading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="room-card skeleton" />
                ))
              ) : (
                <>
                  {activeTab === 'cafe' && (
                    filteredCafeRooms.length > 0 ? (
                      filteredCafeRooms.map(room => (
                        <RoomCard
                          key={room.id}
                          room={room}
                          isJoining={joiningRoomId === room.id}
                          onJoinRoom={handleJoinRoom}
                          canJoin={true}
                          roomType="cafe"
                          onSeeDetails={handleSeeDetails}
                        />
                      ))
                    ) : (
                      <div className="no-rooms-message">
                        <p>No café rooms match your filters. Try different filters or create a new room!</p>
                      </div>
                    )
                  )}

                  {activeTab === 'groups' && (
                    filteredGroupRooms.length > 0 ? (
                      filteredGroupRooms.map(room => (
                        <RoomCard
                          key={room.id}
                          room={room}
                          isJoining={joiningRoomId === room.id}
                          onJoinRoom={handleJoinRoom}
                          canJoin={userGroupIds.has(room.group_id)}
                          roomType="group"
                          onCtaClick={() => window.open('/app/groups', '_blank', 'noopener,noreferrer')}
                          onSeeDetails={handleSeeDetails}
                        />
                      ))
                    ) : (
                      <div className="no-rooms-message">
                        <p>No group rooms match your filters. Join or create a group from the Groups page.</p>
                      </div>
                    )
                  )}

                  {activeTab === 'events' && (
                    filteredEventRooms.length > 0 ? (
                      filteredEventRooms.map(room => (
                        <RoomCard
                          key={room.id}
                          room={room}
                          isJoining={joiningRoomId === room.id}
                          onJoinRoom={handleJoinRoom}
                          canJoin={room.isHost || userEventIds.has(room.eventId)}
                          roomType="event"
                          onCtaClick={() => window.open('/app/events', '_blank', 'noopener,noreferrer')}
                          onSeeDetails={handleSeeDetails}
                        />
                      ))
                    ) : (
                      <div className="no-rooms-message">
                        <p>No event rooms match your filters. Discover or create events from the Events page.</p>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </>
        )}

        {isCreatingRoom && (
          <RoomCreationForm
            onCreate={handleCreateRoom}
            onCancel={() => setIsCreatingRoom(false)}
          />
        )}

        {activeRoom && selectedRoom && (
          <RoomModal onClose={handleLeaveRoom}>
            <VideoChatRoom
              room={selectedRoom}
              onLeave={handleLeaveRoom}
              user={user}
              supabase={supabase}
            />
          </RoomModal>
        )}

        {selectedDetailRoom && (
          <RoomDetailsModal
            room={selectedDetailRoom}
            onClose={() => setSelectedDetailRoom(null)}
            onJoinEntity={handleModalJoin}
          />
        )}
      </div>
    </div>
  );
};

export default VirtualCafe;